import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'

const HoldingFormModal = ({ isOpen, onClose, onSave, initialData = null, accountLabel = '' }) => {
  const [form, setForm] = useState({
    symbol: '',
    exchange: 'NSE',
    quantity: '',
    averagePrice: '',
    ltp: '',
  })

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setForm({
          symbol: initialData.symbol || '',
          exchange: initialData.exchange || 'NSE',
          quantity: String(initialData.quantity || ''),
          averagePrice: String(initialData.averagePrice || ''),
          ltp: initialData.ltp && initialData.ltp !== initialData.averagePrice ? String(initialData.ltp) : '',
        })
      } else {
        setForm({ symbol: '', exchange: 'NSE', quantity: '', averagePrice: '', ltp: '' })
      }
    }
  }, [isOpen, initialData])

  if (!isOpen) return null

  const qty = parseFloat(form.quantity) || 0
  const avg = parseFloat(form.averagePrice) || 0
  const ltp = parseFloat(form.ltp) || avg
  const currentValue = qty * ltp
  const pnl = currentValue - qty * avg
  const pnlPct = qty * avg > 0 ? (pnl / (qty * avg)) * 100 : 0

  const isValid = form.symbol.trim() && qty > 0 && avg > 0

  const handleSave = () => {
    onSave({
      symbol: form.symbol.toUpperCase().trim(),
      exchange: form.exchange,
      isin: initialData?.isin || '',
      quantity: qty,
      t1Quantity: 0,
      averagePrice: avg,
      ltp: parseFloat(form.ltp) || avg,
      currentValue: parseFloat(currentValue.toFixed(2)),
      pnl: parseFloat(pnl.toFixed(2)),
      pnlPercentage: parseFloat(pnlPct.toFixed(2)),
      dayChange: 0,
      dayChangePct: 0,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6 z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {initialData ? 'Edit Holding' : 'Add Holding'}
            </h3>
            {accountLabel && (
              <p className="text-xs text-gray-400 mt-0.5">{accountLabel}</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Symbol *</label>
            <input
              type="text"
              value={form.symbol}
              onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
              placeholder="e.g. RELIANCE"
              className="input w-full"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Exchange</label>
            <select
              value={form.exchange}
              onChange={(e) => setForm({ ...form, exchange: e.target.value })}
              className="input w-full"
            >
              <option value="NSE">NSE</option>
              <option value="BSE">BSE</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
              <input
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                placeholder="0"
                min="0"
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Avg Price *</label>
              <input
                type="number"
                value={form.averagePrice}
                onChange={(e) => setForm({ ...form, averagePrice: e.target.value })}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="input w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              LTP <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="number"
              value={form.ltp}
              onChange={(e) => setForm({ ...form, ltp: e.target.value })}
              placeholder="Leave blank to use Avg Price"
              min="0"
              step="0.01"
              className="input w-full"
            />
            <p className="text-xs text-gray-400 mt-1">If blank, LTP = Avg Price (P&amp;L = ₹0)</p>
          </div>

          {/* Live preview */}
          {isValid && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm border border-gray-100">
              <div className="grid grid-cols-2 gap-y-1.5 text-gray-600">
                <span>Current Value</span>
                <span className="font-semibold text-gray-900 text-right">
                  ₹{currentValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
                <span>P&amp;L</span>
                <span className={`font-semibold text-right ${pnl >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                  {pnl >= 0 ? '+' : ''}₹{pnl.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  <span className="text-xs ml-1 opacity-75">({pnl >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)</span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {initialData ? 'Save Changes' : 'Add Holding'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default HoldingFormModal
